// Reporting & Export — A7.
// Prototype reference: reports.jsx. Live mode uses the Crowd report facade over Core Reporting.

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Operation } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_ZONES } from '../data/mock';
import { useFlags } from '../hooks/useFlags';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
  useCrowdFilterActivity,
} from '../hooks/useCrowdActivityAudit';
import { useReports, type ReportJob, type ReportTemplate } from '../hooks/useReports';
import { useZones } from '../hooks/useZones';
import { PaginationControls, type CrowdPaginationEnvelope } from '../components/PaginationControls';
import { ToastContainer, useToast } from '../components/Toast';
import {
  Btn,
  FONT_SIZE,
  FONT_WEIGHT,
  Icons,
  KLabel,
  LETTER_SPACING,
  LINE_HEIGHT,
  Panel,
  RADIUS,
  SIZE,
  SPACE,
  StatusDot,
  type ColorPalette,
} from '@vigilo/design-system';
import { getCrowdC, operationStatusColor } from '../../design/crowdPalette';
import { isoToLocalDateTimeFieldValue, localDateTimeFieldToIso, toLocalDateTimeFieldValue } from '../utils/local-date-time-range';

type ReportFormat = 'pdf' | 'csv';
type TimePeriod = 'last1h' | 'last6h' | 'last24h' | 'event';
type StatusFilter = 'all' | 'ready' | 'generating' | 'failed';
const PARAM_SEPARATOR = ' | ';

const REPORT_ICON: Record<string, keyof typeof Icons> = {
  zone_activity_summary: 'Activity',
  crowd_trend_report: 'Report',
  alert_history_report: 'Alert',
  operational_summary: 'Shield',
  flagged_device_activity_summary: 'Flag',
};

const REPORT_KIND: Record<string, string> = {
  zone_activity_summary: 'Zone Activity',
  crowd_trend_report: 'Crowd Trend',
  alert_history_report: 'Alert History',
  operational_summary: 'Operations Briefing',
  flagged_device_activity_summary: 'Anonymous Behaviour Log',
};

function nowInput(): string {
  return toLocalDateTimeFieldValue(new Date());
}

function hoursAgoInput(hours: number): string {
  return toLocalDateTimeFieldValue(new Date(Date.now() - hours * 60 * 60_000));
}

function toIso(input: string): string {
  const iso = localDateTimeFieldToIso(input);
  if (!iso) throw new Error('Invalid report date range');
  return iso;
}

function operationStartInput(operation: Operation | null): string {
  if (!operation) return hoursAgoInput(6);
  return isoToLocalDateTimeFieldValue(operation.actualStart ?? operation.scheduledStart);
}

function operationEndInput(operation: Operation | null): string {
  if (!operation) return nowInput();
  return isoToLocalDateTimeFieldValue(operation.actualEnd ?? operation.scheduledEnd);
}

function formatTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function operationWindowLabel(operation: Operation | null): string {
  if (!operation) return 'No operation selected';
  const start = operation.actualStart ?? operation.scheduledStart;
  const end = operation.actualEnd ?? operation.scheduledEnd;
  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
}

function operationStatusLabel(operation: Operation | null): string {
  if (!operation) return 'No operation';
  return operation.status.replace(/_/g, ' ');
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

function zoneLabel(zoneId: unknown, zones: { id: string; name: string }[]): string {
  if (typeof zoneId !== 'string') return 'Selected operation zones';
  const zone = zones.find(z => z.id === zoneId);
  return zone ? zone.name : zoneId;
}

function zoneOptionLabel(z: { name: string; zoneCode?: string }): string {
  return `${z.zoneCode ?? z.name.slice(0, 3).toUpperCase()} - ${z.name}`;
}

function parameterSummary(job: ReportJob, zones: { id: string; name: string }[]): string {
  const { parameters } = job;
  if (job.templateId === 'flagged_device_activity_summary') return 'Anonymous flag token';
  const zone = 'zoneId' in parameters ? zoneLabel(parameters['zoneId'], zones) : 'Selected operation zones';
  const from = typeof parameters['from'] === 'string' ? formatDate(parameters['from']) : null;
  const to = typeof parameters['to'] === 'string' ? formatDate(parameters['to']) : null;
  if (from && to) return `${zone}${PARAM_SEPARATOR}${from} - ${to}`;
  return zone;
}

function jobTitle(job: ReportJob, templates: ReportTemplate[], zones: { id: string; name: string }[]): string {
  const name = templates.find(t => t.templateId === job.templateId)?.displayName
    ?? REPORT_KIND[job.templateId]
    ?? job.templateId.replace(/_/g, ' ');
  if (job.templateId === 'zone_activity_summary') return `${name} - ${zoneLabel(job.parameters['zoneId'], zones)}`;
  return name;
}

function inputStyle(C: ColorPalette): React.CSSProperties {
  return {
    width: '100%',
    padding: `${SPACE[9]}px ${SPACE[12]}px`,
    fontSize: FONT_SIZE.item,
    background: C.surface,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: RADIUS.md,
    fontFamily: "'Albert Sans', sans-serif",
    outline: 'none',
    boxSizing: 'border-box',
  };
}

function Field({ C, label, children }: { C: ColorPalette; label: string; children: ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: FONT_SIZE.xs,
        fontWeight: FONT_WEIGHT.bold,
        letterSpacing: LETTER_SPACING.field,
        textTransform: 'uppercase',
        color: C.muted,
        marginBottom: SPACE[6],
      }}>{label}</div>
      {children}
    </div>
  );
}

function FormatBadge({ C, format }: { C: ColorPalette; format: string }) {
  const isPdf = format.toLowerCase() === 'pdf';
  return (
    <span style={{
      fontSize: FONT_SIZE.label,
      fontWeight: FONT_WEIGHT.bold,
      padding: `${SPACE[2]}px ${SPACE[6]}px`,
      borderRadius: RADIUS.xs,
      background: isPdf ? C.amberDim : C.tealDim,
      color: isPdf ? C.amber : C.teal,
      fontFamily: 'monospace',
      textTransform: 'uppercase',
    }}>{format}</span>
  );
}

function StatusBadge({ C, status }: { C: ColorPalette; status: ReportJob['status'] }) {
  const color = status === 'ready' ? C.green
    : status === 'failed' ? C.ember
    : status === 'generating' ? C.amber
    : C.muted;
  const bg = status === 'ready' ? C.greenDim
    : status === 'failed' ? C.emberDim
    : status === 'generating' ? C.amberDim
    : C.faint;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: SPACE[4],
      fontSize: FONT_SIZE.label,
      fontWeight: FONT_WEIGHT.bold,
      padding: `${SPACE[2]}px ${SPACE[6]}px`,
      borderRadius: RADIUS.sm,
      background: bg,
      color,
      letterSpacing: LETTER_SPACING.wide,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      {status === 'generating' && <StatusDot color={C.amber} pulse size={SIZE.dot} />}
      {status === 'generating' ? 'Generating...' : status}
    </span>
  );
}

function TemplateCard({ C, template, onGenerate }: {
  C: ColorPalette;
  template: ReportTemplate;
  onGenerate: (template: ReportTemplate) => void;
}) {
  const Icon = Icons[REPORT_ICON[template.templateId] ?? 'Report'];
  return (
    <Panel C={C} style={{ padding: SPACE[16], cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACE[10], marginBottom: SPACE[10] }}>
        <div style={{
          width: SIZE.reportIconTile,
          height: SIZE.reportIconTile,
          borderRadius: RADIUS.lg,
          background: C.amberDim,
          border: `1px solid ${C.amber}40`,
          color: C.amber,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={SIZE.icon} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
            {template.displayName}
          </div>
        </div>
      </div>
      <div style={{
        fontSize: FONT_SIZE.sm,
        color: C.muted,
        lineHeight: LINE_HEIGHT.relaxed,
        marginBottom: SPACE[10],
        minHeight: SIZE.reportDescMin,
      }}>{template.description}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: SPACE[4] }}>
          {template.outputFormats.map(format => <FormatBadge key={format} C={C} format={format} />)}
        </div>
        <Btn C={C} variant="primary" size="sm" onClick={() => onGenerate(template)}>
          Generate
        </Btn>
      </div>
    </Panel>
  );
}

function GenerateModal({ C, template, selectedOperation, onClose, onRequest, initialFrom, initialTo, initialZoneId, initialFlagId, initialFormat }: {
  C: ColorPalette;
  template: ReportTemplate;
  selectedOperation: Operation | null;
  onClose: () => void;
  onRequest: (template: ReportTemplate, format: ReportFormat, parameters: Record<string, unknown>) => Promise<void>;
  initialFrom?: string;
  initialTo?:   string;
  initialZoneId?: string;
  initialFlagId?: string;
  initialFormat?: ReportFormat;
}) {
  const { isMock } = useMockMode();
  const { zones } = useZones();
  const flagState = useFlags(undefined, { includeActivity: false });
  const [zoneId, setZoneId] = useState<string>(initialZoneId ?? '');
  const [zoneIds, setZoneIds] = useState<string[]>([]);
  const [flagId, setFlagId] = useState(initialFlagId ?? '');
  const [period, setPeriod] = useState<TimePeriod>('event');
  const [from, setFrom] = useState(initialFrom ?? operationStartInput(selectedOperation));
  const [to, setTo] = useState(initialTo ?? operationEndInput(selectedOperation));
  const [format, setFormat] = useState<ReportFormat | ''>(
    initialFormat && template.outputFormats.includes(initialFormat) ? initialFormat : template.outputFormats[0] ?? '',
  );
  const [generating, setGenerating] = useState(false);
  const assignedZoneIds = useMemo(() => new Set(selectedOperation?.assignedZoneIds ?? []), [selectedOperation]);
  const allZones: { id: string; name: string; zoneCode?: string }[] = isMock ? MOCK_ZONES : zones;
  const availableZones = useMemo(
    () => allZones.filter(zone => assignedZoneIds.has(zone.id)),
    [allZones, assignedZoneIds],
  );
  const availableFlags = useMemo(
    () => flagState.flags.filter(flag => flag.monitoredZoneIds.some(zone => assignedZoneIds.has(zone))),
    [assignedZoneIds, flagState.flags],
  );

  const requiresZone = template.parameters.some(param => param.name === 'zoneId' && param.required);
  const requiresFlag = template.parameters.some(param => param.name === 'flagId' && param.required);
  const requiresDates = template.parameters.some(param => param.name === 'from') || template.parameters.some(param => param.name === 'to');
  const canGenerate = Boolean(selectedOperation?.id)
    && Boolean(format)
    && (!requiresZone || zoneId)
    && (!requiresFlag || flagId)
    && (!requiresDates || (from && to));

  const periodOptions: { v: TimePeriod; l: string }[] = [
    { v: 'last1h', l: 'Last 1 hour' },
    { v: 'last6h', l: 'Last 6 hours' },
    { v: 'last24h', l: 'Last 24 hours' },
    { v: 'event', l: 'Event window' },
  ];

  function applyPeriod(next: TimePeriod) {
    setPeriod(next);
    if (next === 'last1h') setFrom(hoursAgoInput(1));
    if (next === 'last6h') setFrom(hoursAgoInput(6));
    if (next === 'last24h') setFrom(hoursAgoInput(24));
    if (next === 'event') setFrom(operationStartInput(selectedOperation));
    setTo(next === 'event' ? operationEndInput(selectedOperation) : nowInput());
  }

  useEffect(() => {
    if (availableZones.length === 0) {
      // Zones may still be loading: keep an area that was passed in (for example from Movement's Export CSV)
      // until they arrive, instead of clearing it and then defaulting to the first area in the list.
      if (allZones.length > 0) {
        setZoneId('');
        setZoneIds([]);
      }
      return;
    }
    // An empty zone is a valid choice for templates where the zone is optional (Alert History: "All
    // selected-operation zones"), so only force a default when the template requires one. Forcing it
    // for every template made the "All zones" option impossible to keep selected.
    if (zoneId ? !availableZones.some(zone => zone.id === zoneId) : requiresZone) {
      setZoneId(requiresZone ? availableZones[0].id : '');
    }
    setZoneIds(prev => prev.filter(id => assignedZoneIds.has(id)));
  }, [allZones.length, assignedZoneIds, availableZones, requiresZone, zoneId]);

  useEffect(() => {
    if (availableFlags.length === 0) {
      setFlagId('');
      return;
    }
    if (!flagId || !availableFlags.some(flag => flag.flagId === flagId)) {
      setFlagId(availableFlags[0].flagId);
    }
  }, [availableFlags, flagId]);

  function parameters(): Record<string, unknown> {
    const params: Record<string, unknown> = { operationId: selectedOperation?.id };
    if (template.parameters.some(param => param.name === 'zoneId')) params['zoneId'] = zoneId;
    if (template.parameters.some(param => param.name === 'zoneIds') && zoneIds.length > 0) params['zoneIds'] = zoneIds;
    if (template.parameters.some(param => param.name === 'flagId')) params['flagId'] = flagId;
    if (template.parameters.some(param => param.name === 'from')) params['from'] = toIso(from);
    if (template.parameters.some(param => param.name === 'to')) params['to'] = toIso(to);
    return params;
  }

  async function generate() {
    if (!canGenerate || !format) return;
    setGenerating(true);
    try {
      await onRequest(template, format, parameters());
      onClose();
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: `${C.bg}cc`,
      backdropFilter: 'blur(4px)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: SPACE[20],
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: SIZE.reportModal,
        background: C.panel,
        border: `1px solid ${C.border2}`,
        borderRadius: RADIUS.panel,
      }}>
        <div style={{ padding: `${SPACE[16]}px ${SPACE[20]}px`, borderBottom: `1px solid ${C.border2}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold, letterSpacing: LETTER_SPACING.klabel, textTransform: 'uppercase', color: C.amber, marginBottom: SPACE[4] }}>
              Generate Report
            </div>
            <div style={{ fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>{template.displayName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', padding: SPACE[2] }}>
            <Icons.X size={SIZE.icon} />
          </button>
        </div>

        <div style={{ padding: SPACE[20] }}>
          {template.templateId === 'zone_activity_summary' && (
            <Field C={C} label="Zone">
              <select value={zoneId} onChange={e => setZoneId(e.target.value)} style={inputStyle(C)}>
                {availableZones.map(z => <option key={z.id} value={z.id}>{zoneOptionLabel(z)}</option>)}
              </select>
            </Field>
          )}

          {template.templateId === 'crowd_trend_report' && (
            <Field C={C} label="Zone coverage">
              <select
                multiple
                value={zoneIds}
                onChange={e => setZoneIds(Array.from(e.currentTarget.selectedOptions, option => option.value))}
                style={{ ...inputStyle(C), minHeight: SIZE.flagZoneList }}
              >
                {availableZones.map(z => <option key={z.id} value={z.id}>{zoneOptionLabel(z)}</option>)}
              </select>
              <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[6] }}>
                Leave blank for all selected-operation zones.
              </div>
            </Field>
          )}

          {template.templateId === 'alert_history_report' && (
            <Field C={C} label="Zone">
              <select value={zoneId} onChange={e => setZoneId(e.target.value)} style={inputStyle(C)}>
                <option value="">All selected-operation zones</option>
                {availableZones.map(z => <option key={z.id} value={z.id}>{zoneOptionLabel(z)}</option>)}
              </select>
            </Field>
          )}

          {requiresFlag && (
            <Field C={C} label="Flag token">
              <select value={flagId} onChange={e => setFlagId(e.target.value)} style={inputStyle(C)}>
                {availableFlags.map(flag => (
                  <option key={flag.flagId} value={flag.flagId}>
                    {flag.tokenRef.slice(0, 8)}... - {flag.behaviourNote.slice(0, 42)}
                  </option>
                ))}
              </select>
              <div style={{
                marginTop: SPACE[14],
                background: C.amberDim,
                border: `1px solid ${C.amber}40`,
                borderRadius: RADIUS.md,
                padding: `${SPACE[10]}px ${SPACE[12]}px`,
                fontSize: FONT_SIZE.xs,
                color: C.amber,
                lineHeight: LINE_HEIGHT.relaxed,
                display: 'flex',
                gap: SPACE[8],
              }}>
                <Icons.Shield size={SIZE.iconSm} />
                <span>The exported report contains no subscriber identity. Anonymous behaviour log only.</span>
              </div>
            </Field>
          )}

          {requiresDates && (
            <>
              <div style={{ height: SPACE[14] }} />
              <Field C={C} label="Time period">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: SPACE[6] }}>
                  {periodOptions.map(option => (
                    <label key={option.v} style={{
                      padding: `${SPACE[10]}px ${SPACE[12]}px`,
                      borderRadius: RADIUS.md,
                      cursor: 'pointer',
                      background: period === option.v ? C.amberDim : C.surface,
                      border: `1px solid ${period === option.v ? C.amber + '55' : C.border2}`,
                    }}>
                      <input type="radio" checked={period === option.v} onChange={() => applyPeriod(option.v)} style={{ display: 'none' }} />
                      <span style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold, color: period === option.v ? C.amber : C.text }}>{option.l}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <div style={{ height: SPACE[14] }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: SPACE[10] }}>
                <Field C={C} label="From">
                  <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle(C)} />
                </Field>
                <Field C={C} label="To">
                  <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} style={inputStyle(C)} />
                </Field>
              </div>
            </>
          )}

          <div style={{ height: SPACE[14] }} />
          <Field C={C} label="Format">
            <div style={{ display: 'flex', gap: SPACE[6] }}>
              {template.outputFormats.map(f => (
                <label key={f} style={{
                  flex: 1,
                  padding: SPACE[10],
                  borderRadius: RADIUS.md,
                  cursor: 'pointer',
                  textAlign: 'center',
                  background: format === f ? C.amberDim : C.surface,
                  border: `1px solid ${format === f ? C.amber + '55' : C.border2}`,
                  color: format === f ? C.amber : C.text,
                  fontWeight: FONT_WEIGHT.bold,
                  fontSize: FONT_SIZE.body,
                  fontFamily: 'monospace',
                }}>
                  <input type="radio" checked={format === f} onChange={() => setFormat(f)} style={{ display: 'none' }} />
                  {f.toUpperCase()}
                </label>
              ))}
            </div>
          </Field>
        </div>

        <div style={{ padding: `${SPACE[14]}px ${SPACE[20]}px`, borderTop: `1px solid ${C.border2}`, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: FONT_SIZE.xs, color: C.muted, alignSelf: 'center' }}>SHA-256 hash applied - audit-logged</span>
          <div style={{ display: 'flex', gap: SPACE[8] }}>
            <Btn C={C} variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn C={C} variant="primary" icon={<Icons.Download size={SIZE.iconSm} />} onClick={() => { void generate(); }} disabled={!canGenerate || generating}>
              {generating ? 'Generating...' : format ? `Generate ${format.toUpperCase()}` : 'No format available'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_FILTER_OPTIONS: { v: StatusFilter; label: string }[] = [
  { v: 'all',        label: 'All' },
  { v: 'ready',      label: 'Ready' },
  { v: 'generating', label: 'Generating' },
  { v: 'failed',     label: 'Failed' },
];

function JobRow({ C, job, templates, zones, onDownload }: {
  C: ColorPalette;
  job: ReportJob;
  templates: ReportTemplate[];
  zones: { id: string; name: string }[];
  onDownload: (job: ReportJob) => void;
}) {
  return (
    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px` }}>
        <div style={{ fontSize: FONT_SIZE.item, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>{jobTitle(job, templates, zones)}</div>
        <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, fontFamily: 'monospace' }}>{job.id}</div>
        {job.status === 'failed' && job.errorMessage && (
          <div style={{ fontSize: FONT_SIZE.xs, color: C.ember, marginTop: SPACE[4] }}>{job.errorMessage}</div>
        )}
      </td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px`, fontSize: FONT_SIZE.base, color: C.sub }}>{REPORT_KIND[job.templateId] ?? job.templateId}</td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px`, fontSize: FONT_SIZE.base, color: C.sub }}>{parameterSummary(job, zones).split(PARAM_SEPARATOR)[0]}</td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px`, fontSize: FONT_SIZE.base, color: C.sub, fontFamily: 'monospace' }}>{parameterSummary(job, zones).split(PARAM_SEPARATOR)[1] ?? 'Operation window'}</td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px` }}>
        <FormatBadge C={C} format={job.format} />
      </td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px`, fontSize: FONT_SIZE.base, color: C.muted, fontFamily: 'monospace' }}>{formatBytes(job.outputSizeBytes)}</td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px`, fontSize: FONT_SIZE.base, color: C.muted, fontFamily: 'monospace' }}>{formatTime(job.requestedAt)}</td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px` }}>
        <StatusBadge C={C} status={job.status} />
      </td>
      <td style={{ padding: `${SPACE[11]}px ${SPACE[14]}px` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[6] }}>
          {job.status === 'ready' && (
            <Btn C={C} variant="ghost" size="sm" icon={<Icons.Download size={SIZE.iconXs} />} onClick={() => onDownload(job)}>
              Download
            </Btn>
          )}
          {job.status === 'generating' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: SPACE[6], fontSize: FONT_SIZE.xs, color: C.amber }}>
              <StatusDot color={C.amber} pulse size={SIZE.dot} /> Generating...
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function JobTable({ C, allJobs, displayJobs, templates, zones, statusFilter, onFilterChange, pagination, loading, onDownload, onPageChange, onPageSizeChange }: {
  C: ColorPalette;
  allJobs: ReportJob[];
  displayJobs: ReportJob[];
  templates: ReportTemplate[];
  zones: { id: string; name: string }[];
  statusFilter: StatusFilter;
  onFilterChange: (f: StatusFilter) => void;
  pagination: CrowdPaginationEnvelope;
  loading: boolean;
  onDownload: (job: ReportJob) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) {
  return (
    <>
      {/* Filter pills */}
      <div style={{ display: 'flex', gap: SPACE[6], marginBottom: SPACE[10] }}>
        {STATUS_FILTER_OPTIONS.map(opt => {
          const active = statusFilter === opt.v;
          return (
            <button
              key={opt.v}
              onClick={() => onFilterChange(opt.v)}
              style={{
                background: active ? C.amberDim : C.surface,
                border: `1px solid ${active ? C.amber + '55' : C.border2}`,
                borderRadius: RADIUS.pill,
                padding: `${SPACE[4]}px ${SPACE[10]}px`,
                fontSize: FONT_SIZE.xs,
                fontWeight: active ? FONT_WEIGHT.bold : FONT_WEIGHT.semibold,
                color: active ? C.amber : C.sub,
                cursor: 'pointer',
                fontFamily: "'Albert Sans', sans-serif",
                whiteSpace: 'nowrap',
              }}
            >
              {opt.label}{opt.v === 'all' && pagination.total > 0 ? ` (${pagination.total})` : ''}
            </button>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: FONT_SIZE.xs, color: C.muted, alignSelf: 'center' }}>
          Total {pagination.total} reports
        </span>
      </div>

      {/* Empty state */}
      {displayJobs.length === 0 ? (
        <Panel C={C} style={{ padding: SPACE[32], textAlign: 'center' }}>
          <Icons.Report size={SIZE.iconXl} />
          <div style={{ marginTop: SPACE[10], fontSize: FONT_SIZE.body, color: C.muted }}>
            {allJobs.length === 0
              ? 'No reports generated yet'
              : `No ${statusFilter === 'all' ? '' : statusFilter + ' '}reports`}
          </div>
        </Panel>
      ) : (
        <Panel C={C}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1.5px solid ${C.border2}` }}>
                {['Report', 'Type', 'Coverage', 'Period', 'Format', 'Size', 'Generated', 'Status', ''].map(h => (
                  <th key={h} style={{
                    padding: `${SPACE[10]}px ${SPACE[14]}px`,
                    textAlign: 'left',
                    fontSize: FONT_SIZE.label,
                    fontWeight: FONT_WEIGHT.bold,
                    letterSpacing: LETTER_SPACING.field,
                    textTransform: 'uppercase',
                    color: C.muted,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayJobs.map(job => (
                <JobRow
                  key={job.id}
                  C={C}
                  job={job}
                  templates={templates}
                  zones={zones}
                  onDownload={onDownload}
                />
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <PaginationControls
        C={C}
        pagination={pagination}
        loading={loading}
        emptyLabel="No reports generated yet"
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
}

function AuditTrail({
  C,
  jobs,
  templates,
  zones,
  actorName,
}: {
  C: ColorPalette;
  jobs: ReportJob[];
  templates: ReportTemplate[];
  zones: { id: string; name: string }[];
  actorName: string;
}) {
  const displayActor = actorName.trim() || 'Authenticated User';
  const rows = jobs.slice(0, 5).map(job => ({
    ts: formatTime(job.completedAt ?? job.requestedAt),
    actor: displayActor,
    action: job.status === 'ready'
      ? `Generated ${job.format.toUpperCase()}`
      : job.status === 'failed'
        ? 'Generation failed'
        : 'Queued export',
    target: `${job.id} (${jobTitle(job, templates, zones)})`,
    hash: job.outputHash ? `${job.outputHash.slice(0, 4)}...${job.outputHash.slice(-4)}` : '-',
    status: job.status,
  }));

  return (
    <div style={{ marginTop: SPACE[24] }}>
      <KLabel C={C}>Export Audit Trail - Today</KLabel>
      <Panel C={C} style={{ padding: 0 }}>
        {rows.map(row => (
          <div key={`${row.ts}-${row.target}`} style={{ padding: `${SPACE[10]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: SPACE[14] }}>
            <span style={{ fontSize: FONT_SIZE.base, fontFamily: 'monospace', color: C.muted, minWidth: SIZE.reportTimeCol }}>{row.ts}</span>
            <span style={{ fontSize: FONT_SIZE.base, color: C.sub, minWidth: SIZE.reportActorCol }}>{row.actor}</span>
            <span style={{
              fontSize: FONT_SIZE.label,
              fontWeight: FONT_WEIGHT.bold,
              padding: `${SPACE[2]}px ${SPACE[6]}px`,
              borderRadius: RADIUS.sm,
              background: row.status === 'failed' ? C.emberDim : C.tealDim,
              color: row.status === 'failed' ? C.ember : C.teal,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
              minWidth: SIZE.reportActionCol,
              textAlign: 'center',
            }}>{row.action}</span>
            <span style={{ flex: 1, fontSize: FONT_SIZE.base, color: C.text }}>{row.target}</span>
            <span style={{ fontSize: FONT_SIZE.xs, color: C.muted, fontFamily: 'monospace' }}>{row.hash}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}

export function ReportingScreen() {
  const { theme, selectedOperation, currentUser } = useApp();
  const C = getCrowdC(theme);
  const recordActivity = useCrowdActivityAudit();
  const reports = useReports();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMock } = useMockMode();
  const { zones } = useZones();
  const allZones: { id: string; name: string }[] = isMock ? MOCK_ZONES : zones;

  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  useCrowdFilterActivity({
    page: '/reports',
    operationId: selectedOperation?.id,
    filterValues: {
      jobStatus: statusFilter,
    },
  });
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(
    () => searchParams.get('template'),
  );
  const [prefillFrom, setPrefillFrom] = useState<string | null>(
    () => {
      const v = searchParams.get('from');
      return v ? isoToLocalDateTimeFieldValue(v) : null;
    },
  );
  const [prefillTo, setPrefillTo] = useState<string | null>(
    () => {
      const v = searchParams.get('to');
      return v ? isoToLocalDateTimeFieldValue(v) : null;
    },
  );
  const [prefillZoneId, setPrefillZoneId] = useState<string | null>(() => searchParams.get('zoneId'));
  const [prefillFlagId, setPrefillFlagId] = useState<string | null>(() => searchParams.get('flagId'));
  const [prefillFormat, setPrefillFormat] = useState<ReportFormat | null>(() => {
    const v = searchParams.get('format');
    return v === 'pdf' || v === 'csv' ? v : null;
  });
  const isHistoryContext = Boolean(searchParams.get('from') || searchParams.get('to'))
    || selectedOperation?.status === 'closed';

  useEffect(() => {
    if (!pendingTemplateId || reports.templates.length === 0) return;
    const tpl = reports.templates.find(t => t.templateId === pendingTemplateId);
    if (!tpl) return;
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.reportTemplateSelected,
      page: '/reports',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.report,
      resourceId: tpl.templateId,
      metadata: { templateId: tpl.templateId, sourcePage: '/reports' },
    });
    setSelectedTemplate(tpl);
    setPendingTemplateId(null);
    setSearchParams({}, { replace: true });
  }, [pendingTemplateId, recordActivity, reports.templates, selectedOperation?.id, setSearchParams]);

  function selectTemplate(template: ReportTemplate) {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.reportTemplateSelected,
      page: '/reports',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.report,
      resourceId: template.templateId,
      metadata: { templateId: template.templateId, sourcePage: '/reports' },
    });
    setSelectedTemplate(template);
  }

  function changeStatusFilter(next: StatusFilter) {
    setStatusFilter(next);
    reports.setPage(1);
  }

  const jobs = useMemo(
    () => [...reports.jobs].sort((a, b) => Date.parse(b.requestedAt) - Date.parse(a.requestedAt)),
    [reports.jobs],
  );

  const filteredJobs = useMemo(
    () => statusFilter === 'all'
      ? jobs
      : jobs.filter(j =>
          statusFilter === 'generating'
            ? j.status === 'generating' || j.status === 'queued'
            : j.status === statusFilter,
        ),
    [jobs, statusFilter],
  );

  async function handleRequest(template: ReportTemplate, format: ReportFormat, parameters: Record<string, unknown>) {
    try {
      const job = await reports.requestReport(template.templateId, format, parameters);
      toast.show('Report generation started.', 'success');
      const finalJob = await reports.pollJob(job.id);
      if (finalJob.status === 'ready') toast.show('Report ready to download.', 'success');
      if (finalJob.status === 'failed') toast.show(finalJob.errorMessage ?? 'Report generation failed.', 'error');
    } catch {
      toast.show('Report generation failed.', 'error');
    }
  }

  async function handleDownload(job: ReportJob) {
    try {
      await reports.downloadReport(job.id);
      toast.show('Report download started.', 'success');
    } catch {
      toast.show('Download failed.', 'error');
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: SPACE[20], fontFamily: "'Albert Sans', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACE[16] }}>
        <div>
          <div style={{ fontSize: FONT_SIZE.title, fontWeight: FONT_WEIGHT.bold, color: C.text }}>Reporting & Export</div>
          <div style={{ fontSize: FONT_SIZE.base, color: C.muted, marginTop: SPACE[2] }}>
            All exports are logged in the immutable audit trail.
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: SPACE[8],
            marginTop: SPACE[10],
            fontSize: FONT_SIZE.xs,
            color: C.sub,
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: SPACE[6],
              padding: `${SPACE[4]}px ${SPACE[8]}px`,
              borderRadius: RADIUS.sm,
              background: C.surface,
              border: `1px solid ${C.border2}`,
            }}>
              <Icons.Calendar size={SIZE.iconXs} />
              {isHistoryContext ? 'History context' : 'Selected Operation'}
            </span>
            <span style={{ fontWeight: FONT_WEIGHT.bold, color: C.text }}>
              {selectedOperation?.name ?? 'No operation selected'}
            </span>
            <span style={{ color: operationStatusColor(C, selectedOperation?.status), fontWeight: FONT_WEIGHT.bold, textTransform: 'capitalize' }}>
              {operationStatusLabel(selectedOperation)}
            </span>
            <span style={{ fontFamily: 'monospace', color: C.muted }}>
              {operationWindowLabel(selectedOperation)}
            </span>
            <span style={{ color: C.muted }}>
              {selectedOperation ? `${selectedOperation.assignedZoneIds.length} operation zones` : 'Reports require an operation'}
            </span>
          </div>
        </div>
        <Btn C={C} variant="ghost" size="sm" icon={<Icons.Refresh size={SIZE.iconXs} />} onClick={() => { void reports.refreshJobs(); }}>
          Refresh
        </Btn>
      </div>

      <KLabel C={C}>Report Templates</KLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACE[10], marginBottom: SPACE[24] }}>
        {reports.templates.map(template => (
          <TemplateCard key={template.templateId} C={C} template={template} onGenerate={selectTemplate} />
        ))}
      </div>

      <KLabel C={C}>Generated Reports</KLabel>
      <JobTable
        C={C}
        allJobs={jobs}
        displayJobs={filteredJobs}
        templates={reports.templates}
        zones={allZones}
        statusFilter={statusFilter}
        onFilterChange={changeStatusFilter}
        pagination={reports.pagination}
        loading={reports.loading}
        onDownload={job => { void handleDownload(job); }}
        onPageChange={reports.setPage}
        onPageSizeChange={reports.setPageSize}
      />

      <AuditTrail C={C} jobs={jobs} templates={reports.templates} zones={allZones} actorName={currentUser.displayName} />

      {selectedTemplate && (
        <GenerateModal
          C={C}
          template={selectedTemplate}
          selectedOperation={selectedOperation}
          onClose={() => {
            setSelectedTemplate(null);
            setPrefillFrom(null);
            setPrefillTo(null);
            setPrefillZoneId(null);
            setPrefillFlagId(null);
            setPrefillFormat(null);
          }}
          onRequest={handleRequest}
          initialFrom={prefillFrom ?? undefined}
          initialTo={prefillTo ?? undefined}
          initialZoneId={prefillZoneId ?? undefined}
          initialFlagId={prefillFlagId ?? undefined}
          initialFormat={prefillFormat ?? undefined}
        />
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} C={C} />
    </div>
  );
}
